import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import type WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import { when } from 'lit/directives/when.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import { ltnAuthServiceContext } from '../ns-services/contexts.js';

import { ButtressDbService, ButtressEntity, buttressDbServiceContext } from '@buttress/crag';

import { timeOut } from '../ltn-helpers/utils/async.js';
import { Debouncer } from '../ltn-helpers/utils/debouncer.js';
import { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';

import { getDeepValue } from '../helpers/dom.js';
import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

import '../ns-icon/ns-icon.js';
import { callLambda } from '../helpers/lambda.js';
import './ns-loading.js';
import './ns-summary-card.js';
import type { NSSummaryCard } from './ns-summary-card.js';
import { createLogger } from '../ns-services/logger.js';

const logger = createLogger('ns-advanced-search');

@customElement('ns-advanced-search')
export class NSAdvancedSearch extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  private __registrar?: string;

  private __searchTable?: string;

  private __searchProp?: string;

  private __details?: {
    label: string;
    prop: string;
  }[];

  private __noResultsSearchTerm?: boolean = false;

  private __lastSearchTerm?: string;

  @property({ type: String })
  value?: string;

  @query('#search-input')
  private __searchInput?: WaInput;

  @query('#search-button')
  private __searchButton?: HTMLElement;

  @query('#search-element')
  private __searchElement?: HTMLElement;

  @query('#selected-element')
  private __selectedItemElement?: HTMLElement;

  @state()
  private __loading: boolean = false;

  @state()
  private __searchError = '';

  @state()
  private __results: any[] = [];

  @state()
  private __selectedItem?: ButtressEntity;

  @state()
  private __expandedItemId?: string;

  @state()
  private __resultsOpen = false;

  @state()
  private __expandedHeight = 0;

  // Drives our own clear button. wa-input's built-in one (with-clear) hardcodes tabindex="-1",
  // so keyboard users can't reach it.
  @state()
  private __hasValue = false;

  private __debounceSearchInput?: Debouncer;

  private __searchGeneration = 0;

  static styles = [
    nsLayoutStyles,
    nsSharedStyles,
    css`
      button {
        font-size: var(--wa-font-size-s);
        font-weight: 500;
      }

      :host {
        display: block;
        position: relative;
      }

      #search-element.hide {
        display: none;
      }

      #search-input {
        --wa-form-control-value-color: var(--primary-text-color);
      }

      /* Sized and spaced like wa-input's own clear button; the negative block margin keeps the
        24px target from growing the field. */
      #clear-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        /* Matches #search-button: a 20px white disc with a black icon. The 2px padding with
           background-clip keeps the 24px click target while drawing the disc at 20px. */
        background-color: var(--primary-button-text-color);
        background-clip: content-box;
        color: var(--ns-theme-black);

        inline-size: 1.5rem;
        block-size: 1.5rem;
        padding: var(--wa-space-3xs);
        margin-block: -0.25rem;

        font-size: var(--wa-font-size-xs);

        border-radius: var(--wa-border-radius-circle);
      }

      #clear-button:focus-visible,
      #search-button:focus-visible {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: 2px;
      }

      /* The clear button's 2px padding already spaces its disc from the ring. */
      #clear-button:focus-visible {
        outline-offset: 0;
      }

      .search-wrapper {
        position: relative;

        z-index: 2;
      }

      #search-button {
        background-color: var(--primary-button-text-color);

        height: 20px;
        width: 20px;
        padding: 0;
        margin-inline-start: var(--wa-space-2xs);

        border: 0;
        border-radius: var(--wa-border-radius-circle);

        text-align: center;
      }

      #search-button.hide {
        visibility: hidden;
      }

      #results-container {
        background-color: var(--wa-color-surface-lowered);

        height: 0;
        width: 100%;

        border-radius: 0 0 var(--wa-border-radius-m) var(--wa-border-radius-m);

        margin-top: -12.5px;

        overflow: hidden;

        transition: height 0.4s cubic-bezier(0.23, 1, 0.32, 1);
      }

      #results-container.loading-results {
        height: 100px;

        padding: var(--wa-space-l) var(--wa-space-2xs) var(--wa-space-2xs);

        overflow: auto;

        transition: height 0.4s cubic-bezier(0.23, 1, 0.32, 1);
      }

      .result {
        padding-bottom: var(--wa-space-s);
      }

      .padded {
        padding: var(--wa-space-xs);
      }

      #selected-element {
        opacity: 0;

        position: absolute;
        top: 0;
        left: 0;

        width: 100%;
      }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('Unable to find db service');

    if (!this.__auth) throw new Error('Unable to find db service');

    if (this.value) this.__search();

    this.__searchInput?.addEventListener('keyup', (e) => {
      if (e.key === 'Enter' && e.code === 'Enter') {
        this.__search();
      }
    });
  }

  public get results() {
    return this.__results;
  }

  willUpdate(changed: PropertyValues) {
    if (changed.has('value')) this.__hasValue = !!this.value;
  }

  firstUpdated() {
    if (this.hasAttribute('value') && this.value) this.__resetResults();
  }

  public init(
    details: { label: string; prop: string }[],
    registrar?: string,
    collection?: string,
    searchProp?: string,
  ) {
    if (!details || details.length < 1) throw new Error('Missing required details');
    if (!registrar && !collection && !searchProp) throw new Error('Missing required args');

    this.__searchTable = collection;
    this.__searchProp = searchProp;
    this.__details = details;
    this.__registrar = registrar;
  }

  private async __search() {
    this.__debounceSearchInput = Debouncer.debounce(this.__debounceSearchInput, timeOut.after(10), async () => {
      if (!this.__db) throw new Error('Unable to find db service');
      if (!this.__searchInput) throw new Error('Unable to find search input field');
      if (!this.__searchButton) throw new Error('Unable to find search button');

      // init() configures __registrar/__searchTable/__searchProp separately from `value`/connect,
      // so a value can arrive before init() runs. No-op rather than treat it as a real empty
      // result (init() re-triggers a search once it's configured, see below).
      if (!this.__registrar && !(this.__searchTable && this.__searchProp)) return;

      const value = this.__searchInput.value ?? '';
      if (this.__lastSearchTerm && this.__lastSearchTerm?.length > value.length) this.__noResultsSearchTerm = false;

      if (this.__noResultsSearchTerm) return;

      // Marks this invocation as the current one; a later call to __search() or __collapseResults()
      // bumps __searchGeneration again, so this generation can detect it's been superseded and
      // bail out below instead of applying stale results after the user moved on.
      const generation = ++this.__searchGeneration;

      if (!value || value.length < 1) {
        this.__results = [];
        this.__dispatchChangeResultsEvent(value);
        this.__collapseResults();
        return;
      }

      this.__searchButton.classList.add('hide');
      this.__resultsOpen = true;
      this.__loading = true;
      this.__searchError = '';

      // Search for the companies
      let results: ButtressEntity[] = [];
      try {
        if (this.__registrar) {
          results = await this.__pullRegistrarData(value);
        } else if (this.__searchTable && this.__searchProp) {
          const queryCall = await this.__db.query(
            this.__searchTable,
            {
              [this.__searchProp]: {
                $rexi: value,
              },
            },
            {
              limit: 5,
            },
          );
          results = queryCall.results as unknown as ButtressEntity[];
        }
      } catch (err) {
        if (generation !== this.__searchGeneration) return;
        logger.error('Search failed', err);
        this.__results = [];
        this.__loading = false;
        this.__searchError = 'Search failed, please try again.';
        this.__searchButton.classList.remove('hide');
        return;
      }

      if (generation !== this.__searchGeneration) return;

      this.__results = results;
      this.__loading = false;

      if (this.__results.length === 0) this.__noResultsSearchTerm = true;
      this.__lastSearchTerm = value;
      this.__dispatchChangeResultsEvent(value);

      this.__searchButton.classList.remove('hide');

      if (this.__results.length === 1) {
        const [onlyResult] = this.__results;
        const id = onlyResult.id ? onlyResult.id : onlyResult.company_number ? onlyResult.company_number : null;
        if (id) this.__expandedItemId = id;
      }
    });
  }

  private async __pullRegistrarData(value: string): Promise<ButtressEntity[]> {
    if (!this.__auth) throw new Error('Unable to find auth service');

    switch (this.__registrar) {
      case 'COMPANIES_HOUSE':
        const output = await callLambda(
          'companies-house/search-company',
          'GET',
          this.__auth.token,
          undefined,
          `searchQuery=${value}&restrictions=active-companies`,
        );
        const { items } = output.res as unknown as { items: any };
        return items;

      default:
        return [];
    }
  }

  private __dispatchChangeResultsEvent(value: string = '') {
    this.dispatchEvent(
      new CustomEvent('change-results', {
        detail: {
          results: this.__results,
          isValidValue: value && value.length > 0 ? true : false,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async __clearInput() {
    // Invalidates any in-flight __search() so a stale response can't repopulate/re-expand
    // results after the user has explicitly cleared.
    this.__searchGeneration += 1;

    // The superseded search returns before its own cleanup, so end the loading state here.
    this.__loading = false;
    this.__searchButton?.classList.remove('hide');

    this.__collapseResults();

    this.__noResultsSearchTerm = false;
    this.__searchError = '';
    this.__results = [];
    this.__dispatchChangeResultsEvent();
  }

  private __onInput() {
    this.__hasValue = !!this.__searchInput?.value;
    // Emptying the field (backspace, or Escape on type="search") counts as a clear, so an in-flight
    // search can't repopulate the results afterwards.
    if (this.__hasValue) this.__resetResults();
    else this.__clearInput();
  }

  private __onClearClick() {
    if (!this.__searchInput) return;

    this.__searchInput.value = '';
    this.__hasValue = false;
    this.__clearInput();
    this.__searchInput.focus();
  }

  private __resetResults() {
    this.__results = [];
    this.__dispatchChangeResultsEvent();
    this.__collapseResults();
  }

  private __collapseResults() {
    this.__expandedItemId = undefined;
    this.__resultsOpen = false;
  }

  private __toggle(id: string) {
    this.__expandedItemId = this.__expandedItemId === id ? undefined : id;
  }

  protected async updated(changed: PropertyValues) {
    if (!changed.has('__expandedItemId')) return;

    const card = this.renderRoot.querySelector<NSSummaryCard>(`ns-summary-card[data-id='${this.__expandedItemId}']`);
    if (card) await card.updateComplete;
    this.__expandedHeight = card?.detailsHeight ?? 0;
  }

  private __selectItem(id: string) {
    if (this.__selectedItem) return;

    this.__selectedItem = this.__results.find((i) => i.id === id || i.company_number === id);

    if (!this.__searchElement) throw new Error('Unable to find search element');
    if (!this.__selectedItemElement) throw new Error('Unable to find selected item element');

    const timing: KeyframeAnimationOptions = {
      duration: 400,
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      fill: 'forwards',
    };
    this.__searchElement.animate([{ opacity: 1 }, { opacity: 0 }], timing);
    this.__selectedItemElement.animate([{ opacity: 0 }, { opacity: 1 }], timing);

    this.__searchElement.classList.add('hide');

    this.dispatchEvent(
      new CustomEvent('select-item', {
        detail: this.__selectedItem,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private __detailRows(item: ButtressEntity) {
    return (this.__details ?? []).map((row) => ({ label: row.label, value: getDeepValue(item, row.prop) }));
  }

  private __renderSelectedItem() {
    const name = this.__selectedItem?.name ? this.__selectedItem?.name : this.__selectedItem?.title;
    return html`
      <div id="selected-element" aria-label="Selected Item">
        ${when(
          this.__selectedItem,
          () => html`
            <ns-summary-card
              class="w-100"
              heading=${name ?? ''}
              .rows=${this.__detailRows(this.__selectedItem as ButtressEntity)}
              confirmed
            ></ns-summary-card>
          `,
          () => nothing,
        )}
      </div>
    `;
  }

  private __renderSearch() {
    return html`
      <div id="search-element">
        <div class="layout vertical w-100">
          <div role="search" class="search-wrapper">
            <wa-input
              id="search-input"
              .value="${this.value ?? ''}"
              @input="${this.__onInput}"
              class="w-100"
              type="search"
              label="Start Search"
              placeholder="Search"
              autocomplete="off"
            >
              ${when(
                this.__hasValue,
                () => html`
                  <button
                    slot="end"
                    type="button"
                    id="clear-button"
                    class="clickable"
                    @click="${this.__onClearClick}"
                    aria-label="Clear search"
                  >
                    <wa-icon name="xmark" library="system" variant="solid" aria-hidden="true"></wa-icon>
                  </button>
                `,
              )}
              <button
                slot="end"
                type="button"
                id="search-button"
                class="clickable"
                @click="${this.__search}"
                aria-label="Submit search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20px"
                  viewBox="0 0 24 24"
                  width="20px"
                  fill="#000000"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path
                    d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                  />
                </svg>
              </button>
            </wa-input>
          </div>

          <div
            id="results-container"
            class=${this.__resultsOpen ? 'loading-results' : ''}
            style=${this.__resultsOpen && this.__expandedHeight ? `height: ${100 + this.__expandedHeight}px` : ''}
            aria-live="polite"
            aria-busy="${this.__loading}"
          >
            ${when(
              this.__loading,
              () => html`
                <ns-loading class="w-100 h-100 flex" label="Loading..."></ns-loading>
              `,
              () => html`
                <div class="layout vertical center-center w-100 h-100">
                  ${this.__results && this.__results.length > 0
                    ? repeat(
                        this.__results,
                        (item) => (item.id ? item.id : item.company_number ? item.company_number : item),
                        (item) => {
                          const name = item.name ? item.name : item.title ? item.title : 'N/A';
                          const id = item.id ? item.id : item.company_number ? item.company_number : null;
                          if (!id) throw new Error('Could not find id for result item');
                          return html`
                            <ns-summary-card
                              class="result w-100"
                              data-id=${id}
                              heading=${name}
                              expandable
                              .expanded=${id === this.__expandedItemId}
                              .rows=${this.__detailRows(item)}
                              @toggle=${() => this.__toggle(id)}
                              @confirm=${() => this.__selectItem(id)}
                            ></ns-summary-card>
                          `;
                        },
                      )
                    : this.__searchError
                      ? html`
                          <div class="layout vertical w-100 h-100 center-justified padded error-text" role="alert">
                            ${this.__searchError}
                          </div>
                        `
                      : html`
                          ${when(
                            this.__searchInput && this.__searchInput.value && this.__searchInput.value.length > 0,
                            () => html`
                              <div class="layout vertical w-100 h-100 center-justified padded" role="status">
                                <div>Company not found in nodeStream, please create a new company.</div>
                              </div>
                            `,
                          )}
                        `}
                </div>
              `,
            )}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      ${this.__renderSelectedItem()} ${this.__renderSearch()}
    `;
  }
}
